import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Menu, LogOut, Search, Bell, ChevronDown } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { visibleGroups } from '../lib/nav';
import Logo from './Logo';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { cn } from '../lib/utils';

function Sidebar({ collapsed = false, onNavigate }) {
    const { user } = useAuth();
    const groups = visibleGroups(user?.role || '');
    return (
        <aside
            className={cn('bg-white border-r shrink-0 h-full flex flex-col overflow-y-auto', collapsed ? 'w-16' : 'w-64')}
            data-testid="app-sidebar"
        >
            <div className="px-3 py-3 space-y-4">
                {groups.map(group => (
                    <div key={group.key}>
                        {group.label && !collapsed && (
                            <div className="text-[10px] uppercase font-semibold tracking-wider text-neutral-400 px-2 mb-1.5">{group.label}</div>
                        )}
                        {group.label && collapsed && (
                            <div className="mx-2 my-2 border-t border-neutral-200" />
                        )}
                        <nav className="flex flex-col gap-0.5">
                            {group.items.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === '/dharmikdashboard'}
                                    data-testid={item.testid}
                                    onClick={() => onNavigate && onNavigate()}
                                    className={({ isActive }) => cn(
                                        'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-neutral-800 hover:bg-neutral-100',
                                        isActive && 'sidebar-item-active'
                                    )}
                                    title={item.label}
                                >
                                    <item.icon className="h-4 w-4 shrink-0" />
                                    {!collapsed && <span className="truncate">{item.label}</span>}
                                </NavLink>
                            ))}
                        </nav>
                    </div>
                ))}
            </div>
            <div className="mt-auto p-3 text-xs text-neutral-500">
                {!collapsed && (
                    <div className="rounded-lg bg-neutral-50 border p-3">
                        <div className="font-semibold text-neutral-800">Need help?</div>
                        <div className="text-neutral-600 mt-1">Contact your Super Admin for role or access changes.</div>
                    </div>
                )}
            </div>
        </aside>
    );
}

export default function AppShell({ children, pageTitle, pageActions }) {
    const { user, logout } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <div className="min-h-screen flex flex-col bg-neutral-50">
            {/* Green Ticker */}
            <div
                className="brand-green-ticker h-8 sticky top-0 z-50 flex items-center justify-center px-4 text-xs sm:text-sm font-medium"
                data-testid="app-ticker"
            >
                <span className="hidden sm:inline">Welcome back, {user?.name}! </span>
                <span className="sm:ml-1">Manage your nursery business, all in one place.</span>
            </div>

            {/* Blue Header */}
            <header
                className="brand-blue-header sticky top-8 z-40 border-b border-white/10 shadow-sm"
                data-testid="app-header"
            >
                <div className="h-14 sm:h-16 flex items-center gap-2 px-3 sm:px-6">
                    {/* Mobile menu */}
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/10" data-testid="mobile-menu-button">
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="p-0 w-72">
                            <Sidebar onNavigate={() => setMobileOpen(false)} />
                        </SheetContent>
                    </Sheet>

                    <Logo />

                    <div className="ml-auto flex items-center gap-1 sm:gap-2">
                        <div className="hidden md:flex items-center gap-2 bg-white/10 rounded-md px-3 py-1.5 min-w-[220px]">
                            <Search className="h-4 w-4 text-white/70" />
                            <input
                                placeholder="Search orders, products..."
                                className="bg-transparent text-white text-sm outline-none w-full placeholder:text-white/60"
                                data-testid="header-search-input"
                            />
                        </div>
                        <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="header-notifications-button">
                            <Bell className="h-5 w-5" />
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-white/10" data-testid="header-user-menu">
                                    <Avatar className="h-8 w-8">
                                        <AvatarFallback className="bg-brand-green-500 text-white text-xs" style={{ background: 'hsl(142 71% 45%)' }}>
                                            {(user?.name || 'A').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="hidden md:block text-left leading-tight">
                                        <div className="text-sm text-white font-medium">{user?.name}</div>
                                        <div className="text-[11px] text-white/70 capitalize">{user?.role?.replace('_', ' ')}</div>
                                    </div>
                                    <ChevronDown className="h-4 w-4 text-white/70 hidden md:block" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div className="text-sm font-semibold">{user?.name}</div>
                                    <div className="text-xs text-neutral-500">{user?.email}</div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={logout} data-testid="logout-button">
                                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            {/* Body */}
            <div className="flex-1 flex">
                <div className="hidden lg:block">
                    <Sidebar />
                </div>
                <main className="flex-1 min-w-0" data-testid="app-content">
                    {(pageTitle || pageActions) && (
                        <div className="sticky top-[calc(2rem+4rem)] z-30 bg-neutral-50/80 backdrop-blur border-b">
                            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
                                <div>
                                    <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-neutral-900" data-testid="page-title">{pageTitle}</h1>
                                </div>
                                <div className="flex items-center gap-2">{pageActions}</div>
                            </div>
                        </div>
                    )}
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">{children}</div>
                </main>
            </div>
        </div>
    );
}
